// ---------------------------------------------------------------------------
// U2 library structure lookup (G3 — 2026-07-27 design doc §2.1)
//
// Browser-side client for the same-origin Studio proxy
// `POST /api/library-structure-candidates` (functions/api/
// library-structure-candidates.ts). The proxy owns retrieval (kg-worker
// fan-out, fail-closed promoted+admitted filter, patternId dedupe); this
// module owns the authenticated call and a defensive re-validation of the
// response shape.
//
// Fail-open by contract: ANY failure (no session, network, non-200, bad
// shape, timeout) resolves to an empty candidate list — the U2 judgment
// moment is an enrichment of the creator journey and must never block it.
// ---------------------------------------------------------------------------

import { getStudioAccessToken } from '@/services/auth-provider'
import {
  LIBRARY_STRUCTURE_SELECTION_KINDS,
  type LibraryStructureSelectionKind,
} from '@/features/project/lib/library-structure-selection'

export interface LibraryStructureCandidate {
  assetId: string
  kind: LibraryStructureSelectionKind
  patternId: string
  title: string
  snippet: string
  authority: 'promoted'
  status: 'admitted'
  score?: number
  searchableText: string
}

const ENDPOINT = '/api/library-structure-candidates'
// FINDING-5 (adversarial review 2026-07-27) — this bound is the pre-creation
// pause EVERY plain creator submit pays between planning success and project
// creation (the hero passes the judgment callback unconditionally). 6s is
// still generous for a Pages-proxy → kg-worker round-trip (a healthy lookup
// returns well under 1s); it is deliberately SHORTER than the proxy's own
// 8s upstream leg bound, so the browser gives up first and fails open to
// zero candidates rather than stretching the pause to the server's worst
// case.
const LOOKUP_TIMEOUT_MS = 6_000
const DEFAULT_LIMIT = 3

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function toCandidate(value: unknown): LibraryStructureCandidate | null {
  if (!isRecord(value)) return null
  const { assetId, kind, patternId, title, snippet, authority, status, score } =
    value
  if (typeof assetId !== 'string' || !assetId) return null
  if (
    !LIBRARY_STRUCTURE_SELECTION_KINDS.includes(
      kind as LibraryStructureSelectionKind,
    )
  ) {
    return null
  }
  if (typeof patternId !== 'string' || !patternId) return null
  if (typeof title !== 'string' || !title) return null
  if (typeof snippet !== 'string') return null
  // Client-side re-assertion of the proxy's fail-closed admission filter.
  if (authority !== 'promoted' || status !== 'admitted') return null
  const searchableText =
    typeof value.searchableText === 'string' ? value.searchableText : snippet
  return {
    assetId,
    kind: kind as LibraryStructureSelectionKind,
    patternId,
    title,
    snippet,
    authority: 'promoted',
    status: 'admitted',
    ...(typeof score === 'number' && Number.isFinite(score) ? { score } : {}),
    searchableText,
  }
}

export async function fetchLibraryStructureCandidates(
  query: string,
  options: { limit?: number; signal?: AbortSignal } = {},
): Promise<LibraryStructureCandidate[]> {
  const trimmed = query.trim()
  if (!trimmed) return []
  const controller = new AbortController()
  const abortFromCaller = () => controller.abort()
  if (options.signal?.aborted) return []
  options.signal?.addEventListener('abort', abortFromCaller, { once: true })
  const timeout = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS)
  try {
    const accessToken = await getStudioAccessToken()
    if (!accessToken) return []
    const anonKey = (
      import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
    )?.trim()
    const res = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(anonKey ? { apikey: anonKey } : {}),
      },
      body: JSON.stringify({
        query: trimmed,
        limit: options.limit ?? DEFAULT_LIMIT,
      }),
      signal: controller.signal,
    })
    if (!res.ok) return []
    const body: unknown = await res.json()
    if (!isRecord(body) || !Array.isArray(body.candidates)) return []
    return body.candidates
      .map(toCandidate)
      .filter(
        (candidate): candidate is LibraryStructureCandidate =>
          candidate !== null,
      )
      .slice(0, options.limit ?? DEFAULT_LIMIT)
  } catch {
    return []
  } finally {
    clearTimeout(timeout)
    options.signal?.removeEventListener('abort', abortFromCaller)
  }
}
